import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, mergeMap, switchMap, throwError } from 'rxjs';
import { AuthService } from '../oauth/auth.service';

const MAX_RETRY_ATTEMPTS = 1;
const RETRY_HEADER = 'X-Retry-Count';

/**
 * Checks if the access token is expired
 * @param user The user object containing the token
 * @returns true if token is expired or about to expire (within 60 seconds)
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
function isTokenExpired(user: { expires_at?: number } | null): boolean {
  if (!user?.expires_at) {
    return false;
  }

  // Check if token is expired or will expire in the next 60 seconds
  const expirationTime = user.expires_at * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 60 * 1000; // 60 seconds buffer

  return expirationTime - currentTime <= bufferTime;
}

/**
 * Handles request errors, specifically 401 errors by attempting token refresh
 */
function handleRequestError(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  retryCount: number
): ReturnType<HttpHandlerFn> {
  // Only handle 401 errors and prevent infinite retry loops
  if (error.status === 401 && retryCount < MAX_RETRY_ATTEMPTS) {
    // Try to refresh the token and retry the request
    return from(authService.refreshToken()).pipe(
      switchMap((refreshedUser) => {
        if (refreshedUser?.access_token) {
          // Retry the request with the new token
          const retryReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${refreshedUser.access_token}`,
              [RETRY_HEADER]: (retryCount + 1).toString()
            }
          });
          return next(retryReq);
        } else {
          // Refresh failed, clear auth and throw error
          void authService.clearAuth();
          return throwError(() => error);
        }
      }),
      catchError(() => {
        // Refresh failed, clear auth and throw error
        void authService.clearAuth();
        return throwError(() => error);
      })
    );
  }

  // Not a 401, or max retries reached - pass through the error
  return throwError(() => error);
}

export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Only intercept URLs that contain /api/ (works for both relative and absolute URLs)
  if (req.url.includes('/api/')) {
    const user = authService.user();
    const retryCount = parseInt(req.headers.get(RETRY_HEADER) || '0', 10);

    // Check if token is expired before making the request
    if (user && isTokenExpired(user) && retryCount === 0) {
      // Token is expired, refresh it before making the request
      return from(authService.refreshToken()).pipe(
        mergeMap((refreshedUser) => {
          if (refreshedUser?.access_token) {
            // Proceed with the request using the refreshed token
            const modifiedReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${refreshedUser.access_token}`,
                [RETRY_HEADER]: '0'
              }
            });
            return next(modifiedReq).pipe(
              catchError((error: HttpErrorResponse) =>
                handleRequestError(error, req, next, authService, 0)
              )
            );
          } else {
            // Refresh failed, clear auth and proceed without token
            void authService.clearAuth();
            const modifiedReq = req.clone({
              setHeaders: {
                [RETRY_HEADER]: '0'
              }
            });
            return next(modifiedReq);
          }
        }),
        catchError(() => {
          // Refresh failed, clear auth and proceed without token
          void authService.clearAuth();
          const modifiedReq = req.clone({
            setHeaders: {
              [RETRY_HEADER]: '0'
            }
          });
          return next(modifiedReq);
        })
      );
    }

    // Token is not expired (or no user), proceed normally
    const modifiedReq = req.clone({
      setHeaders: {
        ...(user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {}),
        [RETRY_HEADER]: retryCount.toString()
      }
    });

    return next(modifiedReq).pipe(
      catchError((error: HttpErrorResponse) =>
        handleRequestError(error, req, next, authService, retryCount)
      )
    );
  }

  // Pass through for non-api requests
  return next(req);
};
